import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader2, PenLine, Check } from 'lucide-react';
import api from '../../services/api';

let stripePromise = null;
function getStripePromise() {
    if (!stripePromise) {
        stripePromise = api.get('/stripe/config.php').then(({ data }) => loadStripe(data.data.public_key));
    }
    return stripePromise;
}

const formatPrice = (p) => `${parseFloat(p).toFixed(2).replace('.', ',')} €`;

/**
 * Achat d'un pack de corrections rédigées (QRC). Catalogue : pack_offers de
 * type 'qrc' (api/qrc/offers.php). Paiement : Stripe Checkout embarqué via
 * api/qrc/buy.php ; les crédits sont ajoutés par le webhook. Hors production,
 * un bouton « Simuler » crédite sans Stripe.
 *
 * Props : onClose, onSuccess (simulation uniquement : le vrai paiement quitte la page).
 */
export default function QrcPackCheckoutModal({ onClose, onSuccess }) {
    const [offers, setOffers] = useState(null);
    const [balance, setBalance] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [simulating, setSimulating] = useState(false);

    const isDev = import.meta.env.DEV || new URLSearchParams(window.location.search).has('simulate');

    useEffect(() => {
        api.get('/qrc/offers.php')
            .then(({ data }) => {
                const list = data.data?.offers || [];
                setOffers(list);
                setBalance(data.data?.balance ?? null);
                setSelectedId(list[0]?.id || null);
            })
            .catch(e => { setOffers([]); setError(e.response?.data?.message || 'Offres indisponibles'); });
    }, []);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    }, [onClose]);

    const selected = (offers || []).find(o => o.id === selectedId) || null;

    const buy = async () => {
        if (!selected) return;
        setError(null); setLoading(true);
        try {
            const { data } = await api.post('/qrc/buy.php', { offer_id: selected.id });
            if (data.data?.client_secret) setClientSecret(data.data.client_secret);
        } catch (e) { setError(e.response?.data?.message || 'Erreur'); }
        setLoading(false);
    };

    const simulate = async () => {
        if (!selected) return;
        setError(null); setSimulating(true);
        try {
            const { data } = await api.post('/qrc/buy.php', { offer_id: selected.id, simulate: true });
            if (data.data?.simulated) { onSuccess?.(data.data.balance); setTimeout(onClose, 1200); }
        } catch (e) { setError(e.response?.data?.message || 'Erreur'); setSimulating(false); }
    };

    return (
        <div className="checkout-modal-overlay" onClick={onClose}>
            <div className="checkout-modal checkout-modal--wide" onClick={e => e.stopPropagation()}>
                <div className="checkout-modal__header">
                    <span className="checkout-modal__title">
                        {clientSecret && selected ? `Paiement — ${selected.label} (${formatPrice(selected.price_eur)})` : <><PenLine size={18} /> Corrections rédigées</>}
                    </span>
                    <button className="checkout-modal__close" onClick={onClose} aria-label="Fermer"><X size={20} /></button>
                </div>
                <div className="checkout-modal__body">
                    {error && <div className="alert alert-error" style={{ margin: 16 }}>{error}</div>}

                    {simulating && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <div className="spinner" style={{ margin: '0 auto 12px' }} />
                            <p><strong>Paiement simulé</strong> — corrections ajoutées.</p>
                        </div>
                    )}

                    {!clientSecret && !simulating && (
                        <div className="sub-modal-plans">
                            {offers === null && <p className="qrc-attempt-modal__loading"><Loader2 size={15} className="spin" /> Chargement…</p>}
                            {offers && offers.length === 0 && !error && <p>Aucune offre disponible pour le moment.</p>}
                            {offers && offers.length > 1 && (
                                <div className="share-panel__pack-grid">
                                    {offers.map(o => (
                                        <div key={o.id}
                                            className={`share-panel__pack-card ${selectedId === o.id ? 'share-panel__pack-card--active' : ''}`}
                                            onClick={() => setSelectedId(o.id)} style={{ cursor: 'pointer' }}>
                                            <PenLine size={20} />
                                            <div className="share-panel__pack-nb">{o.label}</div>
                                            <div className="share-panel__pack-price">{formatPrice(o.price_eur)}</div>
                                            <div className="share-panel__pack-unit">{o.qrc_credits} corrections</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {selected && (
                                <div className="pack-booster-card">
                                    <div className="pack-booster-card__icon"><PenLine size={28} /></div>
                                    <div className="pack-booster-card__price">{parseFloat(selected.price_eur).toFixed(0)}<span>€</span></div>
                                    <div className="pack-booster-card__label">{selected.description || 'Achat unique — les corrections n\'expirent pas'}</div>
                                    <ul className="pack-booster-card__list">
                                        <li><Check size={14} /><span><strong>+{selected.qrc_credits} corrections</strong> de réponses rédigées, sur n'importe quelle question.</span></li>
                                        <li><Check size={14} /><span>Un verdict détaillé à chaque fois : points acquis, points manquants, réponse attendue.</span></li>
                                        {balance !== null && <li><Check size={14} /><span>Solde actuel : <strong>{balance}</strong> correction{balance > 1 ? 's' : ''}.</span></li>}
                                    </ul>
                                </div>
                            )}
                            <button className="btn btn-primary sub-modal-cta" onClick={buy} disabled={loading || !selected}>
                                {loading ? <><Loader2 size={16} className="spin" /> Chargement…</> : <><CreditCard size={16} /> Acheter{selected ? ` — ${formatPrice(selected.price_eur)}` : ''}</>}
                            </button>
                            {isDev && selected && (
                                <button className="btn btn-sm" onClick={simulate}
                                    style={{ display: 'block', margin: '8px auto 0', fontSize: '0.75em', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}>
                                    Simuler (dev)
                                </button>
                            )}
                            <p className="sub-modal-note">Paiement unique. Les corrections restent acquises, même après la fin d'un abonnement : elles s'utilisent dès que tu es abonné à une thématique.</p>
                        </div>
                    )}

                    {clientSecret && !simulating && (
                        <div>
                            <button className="sub-modal-back" onClick={() => { setClientSecret(null); setError(null); }}>← Annuler</button>
                            <EmbeddedCheckoutProvider stripe={getStripePromise()} options={{ clientSecret }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
